// Küche (Auftrag 2, Block D, 08.10.2026): ob die Küche mitkommt, ob ihr Inhalt
// mitkommt (auch wenn die Küche bleibt) und was mit den Geräten passiert. Ab- und
// Aufbau ist die Leistung "kueche" und wird hier gefragt, wenn die Küche mitkommt.
// Nur bei Wohnungen mit Küche (ablauf.ts).
//
// Vereinfacht (12.10.2026, Sichtprüfung: 10 Geräte × 3 Knöpfe waren zu viel):
// Der häufigste Fall ist vorausgewählt (Küche bleibt, Inhalt kommt mit) und wird
// beim Weiter gespeichert. Geräte als Auswahl zum Antippen: "kommt mit" sofort
// sichtbar, "bleibt" und "entsorgen" aufklappbar. Nicht angetippt = keine Angabe,
// dann entscheiden die Fotos wie bisher.
//
// Seit 30.10.2026 nur noch eine Gerätereihe „bleibt in der Wohnung“: Was mitkommt, sieht die KI auf den Fotos;
// Entsorgen markiert der Kunde im Foto (Seite „Besonderes“). Ältere Angaben "mit"/"entsorgen" bleiben gespeichert.

import { KITCHEN_APPLIANCES, KITCHEN_APPLIANCE_LABEL, type ApplianceAction, type KitchenAppliance, type KitchenInfo } from "@umzugsscan/shared";
import { useNav } from "../Erfassung.tsx";
import { useDraft, useStore } from "../hooks.ts";
import { Check, Choice, Footer } from "../ui/elemente.tsx";

/** Häufigster Fall, vorausgewählt: Küche bleibt in der Wohnung, Geschirr und Vorräte kommen mit. */
const STANDARD = { moves: "nein", contents: "ja" } as const;

export function Kueche() {
  const d = useDraft();
  const store = useStore();
  const { weiter, zurueck } = useNav();
  const k = d.survey.kitchen;
  const assembly = d.survey.services.kueche === true;
  const moves = k.moves ?? STANDARD.moves;
  const contents = k.contents ?? STANDARD.contents;
  const appliances = k.appliances ?? {};

  const set = (patch: Partial<KitchenInfo>) => store.updateSurvey({ kitchen: { ...k, moves, contents, ...patch } });
  /** Ein Gerät steht in höchstens einer Gruppe; nochmal antippen hebt die Angabe auf. */
  const toggle = (key: KitchenAppliance, a: ApplianceAction) => {
    const next = { ...appliances };
    if (next[key] === a) delete next[key];
    else next[key] = a;
    set({ appliances: next });
  };

  const gruppe = (a: ApplianceAction) => (
    <div className="chips" role="group">
      {KITCHEN_APPLIANCES.map((key) => (
        <button key={key} type="button" className={`chip${appliances[key] === a ? " on" : ""}`} aria-pressed={appliances[key] === a} onClick={() => toggle(key, a)}>
          {appliances[key] === a ? "✓ " : ""}
          {KITCHEN_APPLIANCE_LABEL[key]}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <h1>Ihre Küche</h1>
      <p className="lead">Vorausgewählt ist der häufigste Fall. Bitte nur ändern, wenn es bei Ihnen anders ist.</p>
      <section className="card">
        <Choice
          label="Kommt die Küche mit?"
          value={moves}
          options={[
            { value: "nein", label: "Nein, bleibt" },
            { value: "ja", label: "Ja, kommt mit" },
          ]}
          onChange={(v) => {
            set({ moves: v });
            if (v === "nein" && assembly) store.updateSurvey({ services: { kueche: false } });
          }}
        />
        <p className="hint">Gemeint sind Küchenschränke und Arbeitsplatte.</p>
        {moves === "ja" && (
          <Check checked={assembly} onChange={(v) => store.updateSurvey({ services: { kueche: v } })} hint={d.company.privat ? "Die Küche wird ab- und in der neuen Wohnung wieder aufgebaut." : "Wir bauen die Küche ab und in der neuen Wohnung wieder auf."}>
            Küche ab- und aufbauen lassen
          </Check>
        )}
        <Choice
          label="Kommen Geschirr, Töpfe und Vorräte mit?"
          value={contents}
          options={[
            { value: "ja", label: "Ja" },
            { value: "nein", label: "Nein" },
          ]}
          onChange={(v) => set({ contents: v })}
        />
      </section>

      <h2>Bleiben Geräte in der Wohnung?</h2>
      <p className="hint">
        Antippen, was dableibt – auch Waschmaschine und Trockner aus Bad oder Keller. Alles andere auf den Fotos planen wir mit ein. Soll etwas entsorgt
        werden, markieren Sie es im Foto (Schritt „Besonderes“).
      </p>
      <section className="card">{gruppe("bleibt")}</section>
      <Footer
        onBack={zurueck}
        onNext={() => {
          // Vorausgewählte Antworten speichern, auch wenn nichts angetippt wurde.
          if (!k.moves || !k.contents) set({});
          weiter();
        }}
      />
    </>
  );
}
